import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { siteConfig } from '../../config/site';

const Wrap = styled.section`
  position: relative;
  padding: 6rem 1rem 4rem;
  background:
    radial-gradient(1200px 400px at 90% -10%, rgba(0,153,0,0.16), transparent 60%),
    radial-gradient(900px 500px at -10% 110%, rgba(0,153,0,0.10), transparent 60%),
    linear-gradient(180deg, var(--background-dark) 0%, var(--background-light) 100%);
  overflow: hidden;
`;

const Inner = styled.div`
  max-width: 1240px; margin: 0 auto;
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 3.5rem;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const EyebrowRow = styled.div`
  display: flex; align-items: center; gap: 0.75rem;
  margin-bottom: 1.25rem; flex-wrap: wrap;
`;

const Eyebrow = styled.span`
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.4rem 0.85rem; border-radius: 999px;
  background: rgba(0,153,0,0.08); color: var(--primary-color);
  border: 1px solid rgba(0,153,0,0.25);
  font-size: 0.72rem; letter-spacing: 0.14em; font-weight: 700; text-transform: uppercase;
  .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--primary-color); box-shadow: 0 0 10px currentColor; }
`;

const Pill = styled.span`
  display: inline-flex; align-items: center; gap: 0.45rem;
  padding: 0.35rem 0.7rem; border-radius: 999px;
  background: rgba(255,255,255,0.04); color: var(--text-secondary);
  border: 1px solid rgba(255,255,255,0.08);
  font-size: 0.72rem; font-weight: 600;
`;

const Title = styled.h1`
  font-family: 'Georgia', 'Times New Roman', serif;
  font-size: clamp(2.4rem, 5.4vw, 4.25rem);
  line-height: 1.05; letter-spacing: -0.02em;
  color: var(--text-color); margin: 0 0 1.25rem;
  span.hl { color: var(--primary-color); font-style: italic; }
  span.mono { font-family: 'JetBrains Mono', 'SFMono-Regular', ui-monospace, monospace; letter-spacing: -0.04em; font-style: normal; }
`;

const Lede = styled.p`
  font-size: clamp(1.02rem, 1.4vw, 1.18rem);
  line-height: 1.65; color: var(--text-secondary);
  margin: 0 0 1.75rem; max-width: 620px;
  strong { color: var(--text-color); font-weight: 600; }
`;

const CtaRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.85rem; margin-bottom: 2rem;
`;

const Primary = styled(Link)`
  display: inline-flex; align-items: center; gap: 0.6rem;
  background: var(--primary-color); color: var(--text-color);
  padding: 0.95rem 1.6rem; border-radius: var(--border-radius);
  font-weight: 700; text-decoration: none; letter-spacing: 0.01em;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(0,153,0,0.35); }
`;

const Ghost = styled.a`
  display: inline-flex; align-items: center; gap: 0.6rem;
  padding: 0.95rem 1.4rem; border-radius: var(--border-radius);
  background: transparent; color: var(--text-color);
  border: 1px solid rgba(255,255,255,0.14);
  font-weight: 600; text-decoration: none;
  transition: border-color 0.2s ease, background 0.2s ease;
  &:hover { border-color: var(--primary-color); background: rgba(0,153,0,0.06); }
`;

const MetaRow = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem;
  padding-top: 1.75rem; border-top: 1px solid rgba(255,255,255,0.06);
  @media (max-width: 640px) { grid-template-columns: repeat(2, 1fr); }
`;
const Meta = styled.div`
  .k { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 1.35rem; font-weight: 700; color: var(--primary-color); line-height: 1; }
  .v { font-size: 0.78rem; color: var(--text-secondary); margin-top: 0.35rem; letter-spacing: 0.02em; }
`;

/* -------- Right-side visual: editorial card stack -------- */
const Stage = styled.div`
  position: relative;
  height: 520px;
  @media (max-width: 960px) { height: 460px; }
`;

const Frame = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 24px; overflow: hidden;
  border: 1px solid rgba(255,255,255,0.08);
  background: #0e0e0e;
  box-shadow: 0 40px 80px -20px rgba(0,0,0,0.55);
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
  &::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(180deg, transparent 45%, rgba(0,0,0,0.55) 100%);
  }
`;

const Ticker = styled.div`
  position: absolute; left: 1.1rem; right: 1.1rem; top: 1.1rem;
  display: flex; align-items: center; gap: 0.6rem;
  padding: 0.55rem 0.9rem;
  background: rgba(0,0,0,0.55); backdrop-filter: blur(10px);
  border-radius: 12px; border: 1px solid rgba(255,255,255,0.1);
  z-index: 2;
  .dot { width: 8px; height: 8px; border-radius: 50%; background: #22c55e; box-shadow: 0 0 10px #22c55e; animation: pulse 1.6s infinite; }
  .txt { color: #fff; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 0.75rem; letter-spacing: 0.05em; }
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
`;

const InfoCard = styled.div`
  position: absolute; z-index: 3;
  padding: 0.9rem 1rem;
  background: rgba(15,15,15,0.85); backdrop-filter: blur(14px);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 14px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.4);
  min-width: 190px;
  .lbl { font-size: 0.65rem; color: var(--text-secondary); letter-spacing: 0.14em; text-transform: uppercase; margin-bottom: 0.4rem; }
  .val { color: var(--text-color); font-weight: 700; font-size: 0.95rem; display: flex; align-items: center; gap: 0.4rem; }
  .val .chip { font-size: 0.65rem; padding: 0.15rem 0.4rem; border-radius: 4px; background: rgba(0,153,0,0.2); color: var(--primary-color); font-family: 'JetBrains Mono', ui-monospace, monospace; }
`;

const InfoTL = styled(InfoCard)` top: 20%; left: -20px; @media (max-width: 960px) { left: 8px; } `;
const InfoBR = styled(InfoCard)` bottom: 8%; right: -20px; @media (max-width: 960px) { right: 8px; } `;

const EditorialHero: React.FC = () => (
  <Wrap>
    <Inner>
      <div>
        <EyebrowRow>
          <Eyebrow><span className="dot" /> Licensed · Multi-chain</Eyebrow>
          <Pill>Since 2018</Pill>
          <Pill>50+ wallets</Pill>
          <Pill>24/7 desk</Pill>
        </EyebrowRow>

        <Title>
          The store for <span className="hl">flash&nbsp;USDT</span> that behaves like
          <br />
          <span className="mono">TRC-20 · ERC-20 · BEP-20</span>
        </Title>

        <Lede>
          {siteConfig.brand.name} sells a licensed simulation of Tether across three chains — used for
          <strong> product demos, wallet QA, training studios, and short-form video shoots.</strong>{' '}
          Balances render in real wallets and explorers, then expire on your schedule.
        </Lede>

        <CtaRow>
          <Primary to="/pricing">Browse plans →</Primary>
          <Ghost href={`https://wa.me/${siteConfig.contact.whatsapp}`} target="_blank" rel="noopener noreferrer">
            Chat on WhatsApp
          </Ghost>
        </CtaRow>

        <MetaRow>
          <Meta><div className="k">3</div><div className="v">Chains supported</div></Meta>
          <Meta><div className="k">50+</div><div className="v">Wallets compatible</div></Meta>
          <Meta><div className="k">365d</div><div className="v">Max flash validity</div></Meta>
          <Meta><div className="k">2 min</div><div className="v">Avg. delivery time</div></Meta>
        </MetaRow>
      </div>

      <Stage>
        <Frame>
          <img src="/img/editorial-hero.jpg" alt="USDT wallet on device" loading="eager" />
        </Frame>
        <Ticker>
          <span className="dot" />
          <span className="txt">LIVE · flash confirmed · block 68,412,109</span>
        </Ticker>
        <InfoTL>
          <div className="lbl">Wallet balance</div>
          <div className="val">$25,000.00 <span className="chip">TRC-20</span></div>
        </InfoTL>
        <InfoBR>
          <div className="lbl">License key</div>
          <div className="val">USF-••••-•3F8 <span className="chip">active</span></div>
        </InfoBR>
      </Stage>
    </Inner>
  </Wrap>
);

export default EditorialHero;
